/**
 * CR-8227: Platform Config's Organisation section (replaces the Hierarchy
 * Labels card). On a new instance — no customer group or site yet — it sets
 * the organisation up; once one exists it shows the recorded structure, the
 * level names and the customer tree. Helix only: the caller renders it for
 * platform users and nobody else.
 */
import { coreAPI } from '../../../lib/api';
import type { OrganisationStructure } from '../../../lib/api';
import { useFetchData } from '../../../hooks/useFetchData';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { FolderTree, Loader2 } from 'lucide-react';
import { applyHierarchyLabels } from '../../orgchart/types';
import type { PlatformSettings } from './types';
import { OrganisationSetupForm } from './OrganisationSetupForm';
import { OrganisationSummary } from './OrganisationSummary';
import type { LevelNames } from './organisationSetup';

interface OrganisationSectionProps {
  platformSettings: PlatformSettings;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
}

/**
 * @param platformSettings - the page's settings state (structure and level names)
 * @param onPlatformSettingsChange - keeps that state in step with what this section saves
 */
export function OrganisationSection({ platformSettings, onPlatformSettingsChange }: OrganisationSectionProps) {
  const { data: tree, loading, error, refetch } = useFetchData(
    async () => (await coreAPI.getHierarchyTree({ company_type: 'customer' })).data,
  );
  const labels: LevelNames = {
    group: platformSettings.hierarchyLevel1Label,
    division: platformSettings.hierarchyLevel2Label,
    site: platformSettings.hierarchyLevel3Label,
  };
  const hasStructure = (tree?.tree.length ?? 0) > 0;

  const handleSaved = (structure: OrganisationStructure, saved: LevelNames) => {
    onPlatformSettingsChange({
      ...platformSettings,
      organisationStructure: structure,
      hierarchyLevel1Label: saved.group,
      hierarchyLevel2Label: saved.division,
      hierarchyLevel3Label: saved.site,
    });
    applyHierarchyLabels(saved);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FolderTree className="h-5 w-5" />
          Organisation structure
        </CardTitle>
        <CardDescription>
          {hasStructure
            ? 'Only the Helix team changes the structure. Customer users see it read only in Admin Setup > Organisation.'
            : "Set up this customer's organisation: choose its structure, name its levels, then create it."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the organisation…
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-destructive">
            Could not load the organisation: {error}
            <Button variant="outline" size="sm" onClick={refetch}>Try again</Button>
          </div>
        )}
        {!loading && !error && (hasStructure ? (
          <OrganisationSummary
            structure={platformSettings.organisationStructure}
            labels={labels}
            onSaved={handleSaved}
          />
        ) : (
          <OrganisationSetupForm currentLabels={labels} onSettingsSaved={handleSaved} onCreated={refetch} />
        ))}
      </CardContent>
    </Card>
  );
}
